import React, { useState } from 'react';
import cx from 'classnames';
import type SwiperInstance from 'swiper';
import { A11y, FreeMode, Navigation, Pagination, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import cypressIds from 'Components/cypress_ids.json';
import { applyCypressData } from 'Utilities/helpers';

import { Image } from '../Image';
import type { GalleryImageWithTitle } from '../MainGallery/MainGallery';

import {
	BREAKPOINTS,
	DESKTOP_SIZE_BREAKPOINT,
	IMAGE_SIZE,
	LAZY_PRELOAD_IMAGES,
	SPACE_BETWEEN_SLIDES,
} from './GalleryPreview.const';
import { Info } from './Info';
import { Shadow } from './Shadow';

import styles from './GalleryPreview.module.scss';

interface GalleryPreviewProps {
	events?: {
		onClick?: (image: GalleryImageWithTitle) => void;
		onNext?: (image: GalleryImageWithTitle) => void;
		onPrev?: (image: GalleryImageWithTitle) => void;
		onSlideView?: (image: GalleryImageWithTitle) => void;
		onThumbClick?: (image: GalleryImageWithTitle) => void;
	};
	images: GalleryImageWithTitle[];
	overlayBadge?: React.ReactNode;
}

export const GalleryPreview: React.FC<GalleryPreviewProps> = ({ events, images, overlayBadge }) => {
	const [thumbsSwiper, setThumbsSwiper] = useState<SwiperInstance>(null);

	const onNext = (swiper: SwiperInstance) => {
		events?.onNext?.(images[swiper?.activeIndex]);
	};

	const onPrev = (swiper: SwiperInstance) => {
		events?.onPrev?.(images[swiper?.activeIndex]);
	};

	const onSlideChange = (swiper: SwiperInstance) => {
		thumbsSwiper.slideTo(swiper?.activeIndex);
		setTimeout(() => events?.onSlideView?.(images[swiper?.activeIndex]), 0);
	};

	const onClick = (swiper: SwiperInstance) => {
		events?.onClick?.(images[swiper?.clickedIndex]);
	};

	const onThumbClick = (swiper: SwiperInstance) => {
		// eslint-disable-next-line no-unused-expressions
		images[swiper?.clickedIndex] && events?.onThumbClick?.(images[swiper?.clickedIndex]);
	};

	const onSwiper = (swiper: SwiperInstance) => {
		if (images.length) {
			events?.onSlideView?.(images[swiper?.activeIndex]);
		}
	};

	const shouldCenterSlides = thumbsSwiper?.slides?.length > thumbsSwiper?.visibleSlides?.length;

	return (
		<div className={styles.wrapper} {...applyCypressData(cypressIds.containers.mainGallery)}>
			<Swiper
				navigation
				watchSlidesProgress
				breakpoints={BREAKPOINTS}
				className={cx(styles.preview, 'preview-main')}
				lazyPreloadPrevNext={LAZY_PRELOAD_IMAGES}
				modules={[A11y, FreeMode, Pagination, Navigation, Thumbs]}
				onClick={onClick}
				onNavigationNext={onNext}
				onNavigationPrev={onPrev}
				onSlideChange={onSlideChange}
				onSwiper={onSwiper}
				pagination={{
					dynamicBullets: true,
				}}
				spaceBetween={SPACE_BETWEEN_SLIDES}
				thumbs={{ swiper: thumbsSwiper }}
			>
				{images.map(({ id, url }) => (
					<SwiperSlide key={id}>
						<Image
							loading="lazy"
							sizes={`(min-width: ${DESKTOP_SIZE_BREAKPOINT}px) ${IMAGE_SIZE.DESKTOP}px, ${IMAGE_SIZE.TABLET}px`}
							src={url}
							widths={[IMAGE_SIZE.TABLET, IMAGE_SIZE.DESKTOP]}
						/>
					</SwiperSlide>
				))}
				<div className={styles.containerEnd} slot="container-end">
					<Info />
				</div>
				{overlayBadge}
			</Swiper>
			<Swiper
				centerInsufficientSlides
				centeredSlidesBounds
				watchSlidesProgress
				centeredSlides={shouldCenterSlides}
				className={cx(styles.thumbs, 'preview-thumbs')}
				lazyPreloadPrevNext={LAZY_PRELOAD_IMAGES}
				modules={[FreeMode, Navigation, Thumbs]}
				onClick={onThumbClick}
				onSwiper={setThumbsSwiper}
				slidesPerView="auto"
				spaceBetween={SPACE_BETWEEN_SLIDES}
			>
				{images.map(({ id, presets }, index) => (
					<SwiperSlide key={id} data-index={index}>
						<Image loading="lazy" sizes={`${IMAGE_SIZE.THUMB}px`} src={presets.thumbnail} widths={[IMAGE_SIZE.THUMB]} />
					</SwiperSlide>
				))}
				<div className={styles.shadow} slot="container-start">
					<Shadow />
				</div>
			</Swiper>
		</div>
	);
};
